import { memo, useCallback } from 'react'
import { useParams } from 'react-router-dom'

import { useTranslation } from '@crew/modules/i18n'
import { TaskTemplate } from '@crew/models/domain'

import { useModal } from 'components/layouts/modal/useModal'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { TaskTemplateEntryDialog } from 'features/task/components/taskTemplateEntryDialog/taskTemplateEntryDialog'
import {
  ObjectEventMessage,
  notifyTaskTemplateEvent,
} from 'features/app/states/appSlice'
import { useAppDispatch, useAppSelector } from 'states/hooks'
import { NotifyEventType } from 'enums/app'
import { ContractPlan } from '@crew/enums/app'
import classNames from 'classnames'
import { CrewPaidPlanFeature } from 'components/elements/crewPaidPlanFeature/crewPaidPlanFeature'

export const TaskTemplateListToolbar = memo(() => {
  const { projectId } = useParams()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const currentPlan = useAppSelector((state) => state.app.currentPlan)
  const isCurrentPlanFree = currentPlan === ContractPlan.Free

  // タスクテンプレート入力ダイアログのモーダル制御
  const [
    isTaskTemplateEntryDialogOpen,
    openTaskTemplateEntryDialog,
    closeTaskTemplateEntryDialog,
  ] = useModal()

  // 新規テンプレート登録クリック時のイベントハンドラ
  const handleTaskTemplateEntryButtonClick = useCallback(async () => {
    // Freeプランの場合は処理しない
    if (isCurrentPlanFree) return

    // タスクテンプレートロードダイアログを開く
    openTaskTemplateEntryDialog()
  }, [isCurrentPlanFree, openTaskTemplateEntryDialog])

  // 変更イベントハンドラ。登録・削除イベントの共通処理
  const handleChange = useCallback(
    async (eventType: NotifyEventType) => {
      const eventMessage: ObjectEventMessage<TaskTemplate> = {
        eventType,
        // aEventMessageハンドラでは`id`を使用しないため空欄とする
        id: '',
        object: undefined,
      }
      dispatch(notifyTaskTemplateEvent(eventMessage))
    },
    [dispatch]
  )

  // 登録されたイベントハンドラ
  const handleSubmit = useCallback(async () => {
    await handleChange(NotifyEventType.Inserted)
  }, [handleChange])

  // 削除されたイベントハンドラ
  const handleDeleted = useCallback(async () => {
    await handleChange(NotifyEventType.Deleted)
  }, [handleChange])

  // タスクテンプレート登録ボタンのレンダラー
  const renderTaskTemplateEntryButton = useCallback(() => {
    return (
      <CrewPaidPlanFeature
        isCurrentPlanFree={isCurrentPlanFree}
        label={t('action.newTaskTemplate')}
        onClick={handleTaskTemplateEntryButtonClick}
      />
    )
  }, [isCurrentPlanFree, t, handleTaskTemplateEntryButtonClick])

  return (
    <>
      <div className="w-full flex">
        <div className="flex flex-1">
          <CrewButton
            type="primary"
            render={renderTaskTemplateEntryButton}
            className={classNames('ml-auto', {
              'opacity-50': isCurrentPlanFree,
            })}
          />
        </div>
      </div>

      {projectId && (
        <TaskTemplateEntryDialog
          isEditMode={false}
          title={t('label.taskTemplate.registerTaskTemplate')}
          projectId={projectId}
          isOpen={isTaskTemplateEntryDialogOpen}
          onClose={closeTaskTemplateEntryDialog}
          onSubmit={handleSubmit}
          onDeleted={handleDeleted}
        />
      )}
    </>
  )
})
